import { Link } from "react-router-dom";
import { Phone, CalendarCheck, Handshake, Tag, House, ArrowRight, Clock, ShieldCheck, MapPin, Users, Star, ChatsCircle, Bell } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Home = () => {
  const services = [
    {
      icon: House,
      title: "In-Home Estate Sales",
      description: "Professional estate sales conducted right in the home, making it easy for buyers and maximizing your returns."
    },
    {
      icon: Users,
      title: "Living Estate Sales",
      description: "Downsizing while still living? We help you sell what you no longer need with respect and care."
    },
    {
      icon: Tag,
      title: "Downsizing Help",
      description: "Moving to a smaller home or assisted living? We make the transition smoother by handling your belongings."
    },
    {
      icon: ShieldCheck,
      title: "Estate Liquidation",
      description: "Complete liquidation services for executors, heirs, and families managing an estate."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Schedule a Consultation",
      description: "Call us or fill out our contact form. We'll discuss your situation and schedule a time to view the home."
    },
    {
      number: "02",
      title: "We Sort, Stage & Price",
      description: "Our team handles everything—sorting items, researching values, pricing fairly, and staging attractively."
    },
    {
      number: "03",
      title: "We Host the Sale",
      description: "We advertise, manage the sale days, handle all transactions, and help maximize your results."
    }
  ];

  const reasons = [
    { icon: Clock, text: "9 years serving the Fargo-Moorhead region" },
    { icon: House, text: "No need to clean or organize first" },
    { icon: Handshake, text: "Compassionate, respectful service" },
    { icon: MapPin, text: "Local knowledge of the regional market" },
    { icon: ShieldCheck, text: "Professional pricing and staging" },
    { icon: Users, text: "Easy communication throughout" }
  ];

  const towns = [
    { name: "Fargo, ND", slug: "fargo-nd" },
    { name: "Moorhead, MN", slug: "moorhead-mn" },
    { name: "West Fargo, ND", slug: "west-fargo-nd" },
    { name: "Detroit Lakes, MN", slug: "detroit-lakes-mn" },
    { name: "Fergus Falls, MN", slug: "fergus-falls-mn" },
    { name: "Horace, ND", slug: "horace-nd" }
  ];

  const faqs = [
    {
      question: "Do I need to clean the house before calling?",
      answer: "No! This is one of the most common misconceptions. You do not need to clean, sort, or organize anything before contacting us. We specialize in walking into homes exactly as they are and handling everything from there."
    },
    {
      question: "How far in advance should I schedule?",
      answer: "Ideally, contact us 3-4 weeks before you need the sale completed. However, we understand circumstances vary and do our best to accommodate urgent situations."
    },
    {
      question: "What areas do you serve?",
      answer: "We serve Fargo and surrounding communities within about 60 miles, including Moorhead, West Fargo, Detroit Lakes, Fergus Falls, and many more."
    }
  ];

  const testimonials = [
    {
      name: "Margaret S.",
      location: "Fargo, ND",
      text: "After my mother passed, I was overwhelmed with her belongings. Legacy Liquidators handled everything with such care and professionalism. They made a difficult time so much easier."
    },
    {
      name: "Robert & Linda K.",
      location: "Moorhead, MN",
      text: "We were downsizing to a smaller home and didn't know where to start. The team sorted through decades of belongings and the sale went smoothly. Highly recommend!"
    },
    {
      name: "James T.",
      location: "West Fargo, ND",
      text: "Professional, compassionate, and thorough. They found value in items I would have thrown away. The whole process was stress-free."
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://customer-assets.emergentagent.com/job_legacy-liquidators/artifacts/1pxk21so_f781b3b8-f316-4c7f-89f8-b8ba4a3df6e3.png')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C1914]/80 via-[#1C1914]/50 to-transparent" />
        
        <div className="relative z-10 px-6 md:px-12 lg:px-24 py-20 w-full max-w-4xl">
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-6 animate-fade-in">
            Fargo-Moorhead's Trusted Estate Sale Company | 9 Years Experience
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-white leading-tight mb-6 animate-fade-in-up">
            Compassionate Estate Sale Services in Fargo, ND & Moorhead, MN
          </h1>
          <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-8 max-w-2xl animate-fade-in-up animation-delay-100">
            Kathleen (Kathy) Schultz, owner of Legacy Liquidators, has helped families across Fargo, Moorhead, West Fargo, Detroit Lakes, Fergus Falls, and nearby communities hold professional in-home estate sales with care, respect, and less stress. <span className="font-semibold">No need to clean first</span>—we do the work for you!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
            <a
              href="tel:2182982420"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="hero-cta-call"
            >
              <Phone weight="fill" className="w-5 h-5" />
              Call 218-298-2420
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="hero-cta-consultation"
            >
              Request Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
            Professional Estate Sale Services in Fargo-Moorhead
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
            How We Help Families with Estate Sales in Fargo, ND
          </h2>
          <p className="text-lg text-[#5C564D] max-w-2xl mb-12">
            From in-home estate sales to complete estate liquidation services, Legacy Liquidators LLC provides compassionate support during life's transitions. Serving Fargo, Moorhead, West Fargo, Detroit Lakes, Fergus Falls, and surrounding communities.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white border border-[#E2DCD0] p-8 rounded-lg card-hover"
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-lg flex items-center justify-center mb-6">
                  <service.icon weight="duotone" className="w-6 h-6 text-[#2C4C3B]" />
                </div>
                <h3 className="text-xl font-serif font-medium text-[#1C1914] mb-3">{service.title}</h3>
                <p className="text-[#5C564D] text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
                The Process
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
                How It Works
              </h2>
              <p className="text-lg text-[#5C564D] mb-10">
                We make the estate sale process simple and stress-free. Here's what to expect when you work with Legacy Liquidators LLC.
              </p>
              
              <div className="space-y-8">
                {steps.map((step, index) => (
                  <div key={index} className="flex gap-6" data-testid={`process-step-${index}`}>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#2C4C3B] text-white font-serif text-lg font-semibold">
                        {step.number}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-xl font-serif font-medium text-[#1C1914] mb-2">{step.title}</h3>
                      <p className="text-[#5C564D] leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/12903031/pexels-photo-12903031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Estate sale consultation in Fargo ND - Legacy Liquidators LLC professional estate sale company helps families with in-home estate sales, downsizing, and estate liquidation services in Fargo, Moorhead, West Fargo, and surrounding communities"
                className="rounded-lg shadow-lg w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#C5A059] text-[#1C1914] p-6 rounded-lg shadow-lg max-w-xs hidden lg:block">
                <p className="font-serif text-lg font-medium">"No need to clean first—we handle everything."</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#2C4C3B]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              Why Families Trust Us
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-white mb-6">
              Why Families Choose Legacy Liquidators
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              We've helped hundreds of families across the Fargo-Moorhead region through difficult transitions with compassion and professionalism.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reasons.map((reason, index) => (
              <div
                key={index}
                className="flex items-center gap-4 bg-white/10 backdrop-blur p-6 rounded-lg"
                data-testid={`reason-${index}`}
              >
                <reason.icon weight="duotone" className="w-8 h-8 text-[#C5A059] flex-shrink-0" />
                <span className="text-white font-medium">{reason.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              Testimonials
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
              What Families Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white border border-[#E2DCD0] p-8 rounded-lg"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} weight="fill" className="w-5 h-5 text-[#C5A059]" />
                  ))}
                </div>
                <p className="text-[#5C564D] leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-[#1C1914]">{testimonial.name}</p>
                  <p className="text-sm text-[#5C564D]">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
            Estate Sale Service Areas in North Dakota & Minnesota
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
            Estate Sales in Fargo, Moorhead & Surrounding Communities
          </h2>
          <p className="text-lg text-[#5C564D] max-w-2xl mb-12">
            Legacy Liquidators LLC provides professional estate sale services throughout the Fargo-Moorhead metro and surrounding communities within about 60 miles. Find estate sales today, tomorrow, and this weekend in your area.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {towns.map((town, index) => (
              <Link
                key={index}
                to={`/service-areas/${town.slug}`}
                className="bg-[#F7F5F0] hover:bg-[#2C4C3B] border border-[#E2DCD0] p-4 rounded-lg text-center transition-all duration-300 group"
                data-testid={`town-link-${town.slug}`}
              >
                <MapPin weight="duotone" className="w-6 h-6 text-[#C5A059] mx-auto mb-2" />
                <span className="text-sm font-medium text-[#1C1914] group-hover:text-white transition-colors">{town.name}</span>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link
              to="/service-areas"
              className="inline-flex items-center gap-2 text-[#2C4C3B] hover:text-[#1E3629] font-medium transition-colors"
              data-testid="view-all-areas-link"
            >
              View All Service Areas
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
              Estate Sale FAQ - Fargo ND
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
              Common Questions About Estate Sales in Fargo-Moorhead
            </h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border border-[#E2DCD0] rounded-lg px-6 md:px-8"
              >
                <AccordionTrigger
                  className="text-left text-[#1C1914] font-medium py-6 hover:no-underline text-lg md:text-xl"
                  data-testid={`faq-trigger-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C564D] pb-6 text-base md:text-lg leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-[#2C4C3B] hover:text-[#1E3629] font-medium text-lg transition-colors"
              data-testid="view-all-faqs-link"
            >
              View All FAQs
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Buyers Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/9704112/pexels-photo-9704112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Estate sales today in Fargo ND - antique furniture, vintage collectibles, and household items available at Legacy Liquidators estate sales this weekend in Fargo, Moorhead, West Fargo, and surrounding areas"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
                Estate Sales Today & This Weekend
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#1C1914] mb-6">
                Find Estate Sales in Fargo, Moorhead & West Fargo
              </h2>
              <p className="text-lg text-[#5C564D] mb-8">
                View estate sales today, tomorrow, and this weekend throughout Fargo-Moorhead, Detroit Lakes, Fergus Falls, and surrounding communities. Subscribe to never miss an estate sale!
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3 text-[#5C564D]">
                  <CalendarCheck weight="duotone" className="w-6 h-6 text-[#2C4C3B]" />
                  <span>View estate sales today and this weekend in Fargo area</span>
                </li>
                <li className="flex items-center gap-3 text-[#5C564D]">
                  <Bell weight="duotone" className="w-6 h-6 text-[#2C4C3B]" />
                  <span>Subscribe to get notified of upcoming estate sales</span>
                </li>
                <li className="flex items-center gap-3 text-[#5C564D]">
                  <MapPin weight="duotone" className="w-6 h-6 text-[#2C4C3B]" />
                  <span>Estate sales throughout North Dakota and Minnesota</span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/sales"
                  className="inline-flex items-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-medium"
                  data-testid="view-sales-button"
                >
                  View Current Sales
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="https://estatesales.org/subscriber/company/add/25580"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 py-4 rounded-md transition-all duration-300 font-medium"
                  data-testid="subscribe-sales-button"
                >
                  <Bell weight="fill" className="w-5 h-5" />
                  Subscribe for Alerts
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#1C1914]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Contact Legacy Liquidators LLC today for a free consultation. We're here to help make a hard time easier.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:2182982420"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="cta-banner-phone"
            >
              <Phone weight="fill" className="w-5 h-5" />
              Call 218-298-2420
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="cta-banner-contact"
            >
              Request a Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
